/**
 * Cohort Finalization Service
 * Handles the finalization of approved cohorts by calling the finalizeCohort Cloud Function
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

// Interface for finalizeCohort input
interface FinalizeCohortInput {
  cohortId: string;
}

// Interface for finalizeCohort response
interface FinalizeCohortResponse {
  success: boolean;
  message?: string;
  error?: string;
  stats?: {
    cohortNumber: number;
    totalSellers: number;
    sellersProcessed: number;
    targetLedgerRecords: number;
    cohortLedgerRecords: number;
    firestoreWrites: number;
    sheetsWrites: number;
  };
}

/**
 * Service class for cohort finalization operations
 */
export class CohortFinalizationService {
  private static finalizeCohortFunction = httpsCallable<FinalizeCohortInput, FinalizeCohortResponse>(
    functions,
    'finalizeCohort'
  );

  /**
   * Finalize an approved cohort by moving it to the ledgers
   * @param cohortId - The ID of the cohort to finalize
   * @returns Promise with the finalization result
   */
  static async finalizeCohort(cohortId: string): Promise<FinalizeCohortResponse> {
    try {
      console.log(`🚀 Starting cohort finalization for: ${cohortId}`);
      
      const result = await this.finalizeCohortFunction({ cohortId });
      
      if (result.data.success) {
        console.log(`✅ Cohort finalization completed successfully:`, result.data.stats);
        return result.data;
      } else {
        console.error(`❌ Cohort finalization failed:`, result.data.error);
        throw new Error(result.data.error || 'Finalization failed');
      }
    } catch (error) {
      console.error(`❌ Error finalizing cohort ${cohortId}:`, error);
      
      // Handle different types of errors
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('An unexpected error occurred during cohort finalization');
      }
    }
  }

  /**
   * Validate that a cohort can be finalized
   * @param cohortStatus - The current status of the cohort
   * @returns boolean indicating if the cohort can be finalized
   */
  static canFinalizeCohort(cohortStatus: string): boolean {
    return cohortStatus === 'APPROVED';
  }

  /**
   * Get user-friendly status message for cohort finalization
   * @param cohortStatus - The current status of the cohort
   * @returns string with status message
   */
  static getFinalizationStatusMessage(cohortStatus: string): string {
    switch (cohortStatus) {
      case 'IN_REVIEW':
        return 'Cohort is still being reviewed. Complete all problem resolution first.';
      case 'APPROVED':
        return 'Cohort is ready to be finalized and moved to ledgers.';
      case 'FINALIZED':
        return 'Cohort has already been finalized and moved to ledgers.';
      default:
        return `Unknown cohort status: ${cohortStatus}`;
    }
  }
}

export default CohortFinalizationService;